import { useMutation, useQueryClient } from "@tanstack/react-query";

import { useApiClient } from "~/hooks/use-api-client";
import { trackEvent } from "~/lib/analytics";
import { markSurveyCompleted } from "./use-active-surveys";

// AIDEV-NOTE: Hook to save survey responses
// Creates/updates response record with partial answers, marks complete when finished

interface SaveResponseOptions {
    surveyId: string;
    surveyTitle: string;
}

interface SaveResponseParams {
    responses: Record<string, string | string[]>;
    isComplete: boolean;
    questionId?: string;
    questionType?:
        | "multiple_choice"
        | "multiple_select"
        | "short_text"
        | "long_text";
}

export function useSaveResponse({
    surveyId,
    surveyTitle,
}: SaveResponseOptions) {
    const apiClient = useApiClient();
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: async ({ responses, isComplete }: SaveResponseParams) => {
            const response = await apiClient.post(
                `/surveys/${surveyId}/respond`,
                {
                    responses,
                    isComplete,
                },
            );

            if (!response.ok) {
                throw new Error("Failed to save response");
            }

            return response.json();
        },
        onSuccess: (_, variables) => {
            // AIDEV-NOTE: Mark as completed FIRST to prevent race condition
            // This ensures client-side filtering happens before query invalidation refetch
            if (variables.isComplete) {
                markSurveyCompleted(surveyId);
            }

            // AIDEV-NOTE: Invalidate ALL active survey queries (all paths)
            // Query key is ["surveys", "active", path] so we need to match the prefix
            queryClient.invalidateQueries({
                queryKey: ["surveys", "active"],
                exact: false, // Match all queries starting with this key
            });

            // AIDEV-NOTE: On completion, refresh eligible triggers since completion may unblock new triggers
            if (variables.isComplete) {
                queryClient.invalidateQueries({
                    queryKey: ["surveys", "eligible-triggers"],
                });
            }

            // Track analytics
            if (variables.isComplete) {
                trackEvent("survey_completed", {
                    surveyId,
                    surveyTitle,
                    questionsAnswered: Object.keys(variables.responses).length,
                });
            } else if (variables.questionId && variables.questionType) {
                trackEvent("survey_question_answered", {
                    surveyId,
                    questionId: variables.questionId,
                    questionType: variables.questionType,
                });
            }
        },
    });
}
